import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import BillingHealthPanel from "@/components/admin/BillingHealthPanel";
import { Users, Mail, Download, Search, CheckCircle2, XCircle } from "lucide-react";

function toCSV(rows, columns) {
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = columns.map((c) => escape(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => escape(c.get(r))).join(",")).join("\n");
  return header + "\n" + body;
}

function download(filename, contents) {
  const blob = new Blob([contents], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function AdminSubscribers() {
  const [members, setMembers] = useState([]);
  const [emailSubs, setEmailSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mSearch, setMSearch] = useState("");
  const [eSearch, setESearch] = useState("");

  useEffect(() => {
    Promise.all([api.get("/admin/members"), api.get("/admin/subscribers")])
      .then(([m, s]) => {
        setMembers(m.data || []);
        setEmailSubs(s.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredMembers = useMemo(() => {
    if (!mSearch.trim()) return members;
    const q = mSearch.toLowerCase();
    return members.filter((m) => (m.email || "").toLowerCase().includes(q) || (m.name || "").toLowerCase().includes(q));
  }, [members, mSearch]);

  const filteredEmails = useMemo(() => {
    if (!eSearch.trim()) return emailSubs;
    const q = eSearch.toLowerCase();
    return emailSubs.filter((s) => (s.email || "").toLowerCase().includes(q) || (s.source || "").toLowerCase().includes(q));
  }, [emailSubs, eSearch]);

  const activeCount = members.filter((m) => m.status === "active").length;
  const lapsedCount = members.length - activeCount;

  const downloadMembers = () => {
    download(
      `paid-members-${new Date().toISOString().slice(0, 10)}.csv`,
      toCSV(members, [
        { label: "Name", get: (m) => m.name },
        { label: "Email", get: (m) => m.email },
        { label: "Status", get: (m) => m.status },
        { label: "Membership expires", get: (m) => m.membership_expires_at },
        { label: "Joined", get: (m) => m.created_at },
        { label: "Stripe customer", get: (m) => m.stripe_customer_id },
      ])
    );
  };

  const downloadEmails = () => {
    download(
      `email-subscribers-${new Date().toISOString().slice(0, 10)}.csv`,
      toCSV(emailSubs, [
        { label: "Email", get: (s) => s.email },
        { label: "Source", get: (s) => s.source },
        { label: "Subscribed", get: (s) => s.subscribed_at },
      ])
    );
  };

  if (loading) return <p style={{ color: "var(--muted)" }}>Loading subscribers…</p>;

  return (
    <div className="space-y-10" data-testid="admin-subscribers">
      <div>
        <h2 className="font-serif-display text-2xl lg:text-3xl mb-1" style={{ color: "var(--ink)" }}>
          Your people
        </h2>
        <p className="text-xs" style={{ color: "var(--muted)" }}>
          Everyone paying for a membership + everyone who signed up for your newsletter.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-surface p-5">
          <div className="overline mb-2" style={{ fontSize: "0.6rem" }}>Active members</div>
          <div className="font-serif-display text-4xl leading-none" style={{ color: "var(--ember)" }}>{activeCount}</div>
          <p className="mt-2 text-xs" style={{ color: "var(--muted)" }}>${(activeCount * 4.99).toFixed(2)}/mo baseline</p>
        </div>
        <div className="card-surface p-5">
          <div className="overline mb-2" style={{ fontSize: "0.6rem" }}>Lapsed / cancelled</div>
          <div className="font-serif-display text-4xl leading-none" style={{ color: "var(--muted)" }}>{lapsedCount}</div>
          <p className="mt-2 text-xs" style={{ color: "var(--muted)" }}>Previously paid</p>
        </div>
        <div className="card-surface p-5">
          <div className="overline mb-2" style={{ fontSize: "0.6rem" }}>Total members ever</div>
          <div className="font-serif-display text-4xl leading-none" style={{ color: "var(--ink)" }}>{members.length}</div>
          <p className="mt-2 text-xs" style={{ color: "var(--muted)" }}>All-time</p>
        </div>
        <div className="card-surface p-5">
          <div className="overline mb-2" style={{ fontSize: "0.6rem" }}>Email subscribers</div>
          <div className="font-serif-display text-4xl leading-none" style={{ color: "var(--terracotta)" }}>{emailSubs.length}</div>
          <p className="mt-2 text-xs" style={{ color: "var(--muted)" }}>Newsletter list</p>
        </div>
      </div>

      <BillingHealthPanel />

      {/* Paid Members */}
      <section className="card-surface p-6" data-testid="admin-members-panel">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
          <div className="flex items-center gap-3">
            <Users size={20} strokeWidth={1.75} style={{ color: "var(--ember)" }} />
            <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Paid members</h3>
            <span className="text-xs" style={{ color: "var(--muted)" }}>{filteredMembers.length} of {members.length}</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--muted)" }} />
              <input
                type="text"
                placeholder="Search email or name…"
                value={mSearch}
                onChange={(e) => setMSearch(e.target.value)}
                data-testid="admin-members-search"
                className="pl-8 pr-3 py-2 text-sm border outline-none"
                style={{ borderColor: "var(--border-subtle)", background: "var(--bg-alt)", minWidth: "18rem" }}
              />
            </div>
            <button
              onClick={downloadMembers}
              data-testid="admin-members-download"
              className="btn-ghost"
              style={{ padding: "0.5rem 0.9rem", fontSize: "0.7rem" }}
              disabled={members.length === 0}
            >
              <Download size={12} /> CSV
            </button>
          </div>
        </div>

        {filteredMembers.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--muted)" }}>No members yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[0.6rem] uppercase tracking-wider text-left" style={{ color: "var(--muted)" }}>
                  <th className="pb-2 font-medium">Name</th>
                  <th className="pb-2 font-medium">Email</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium">Renews / Expires</th>
                  <th className="pb-2 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((m) => (
                  <tr key={m.id} className="border-t" style={{ borderColor: "var(--border-subtle)" }} data-testid={`admin-member-row-${m.id}`}>
                    <td className="py-3 pr-3" style={{ color: "var(--ink)" }}>{m.name || "—"}</td>
                    <td className="py-3 pr-3" style={{ color: "var(--ink-2)" }}>{m.email}</td>
                    <td className="py-3 pr-3">
                      {m.status === "active" ? (
                        <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: "var(--olive)" }}>
                          <CheckCircle2 size={13} /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: "var(--muted)" }}>
                          <XCircle size={13} /> Lapsed
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-3" style={{ color: "var(--ink-2)" }}>{fmtDate(m.membership_expires_at)}</td>
                    <td className="py-3" style={{ color: "var(--muted)" }}>{fmtDate(m.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Email Subscribers */}
      <section className="card-surface p-6" data-testid="admin-email-subs-panel">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
          <div className="flex items-center gap-3">
            <Mail size={20} strokeWidth={1.75} style={{ color: "var(--terracotta)" }} />
            <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Newsletter subscribers</h3>
            <span className="text-xs" style={{ color: "var(--muted)" }}>{filteredEmails.length} of {emailSubs.length}</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--muted)" }} />
              <input
                type="text"
                placeholder="Search email or source…"
                value={eSearch}
                onChange={(e) => setESearch(e.target.value)}
                data-testid="admin-emails-search"
                className="pl-8 pr-3 py-2 text-sm border outline-none"
                style={{ borderColor: "var(--border-subtle)", background: "var(--bg-alt)", minWidth: "18rem" }}
              />
            </div>
            <button
              onClick={downloadEmails}
              data-testid="admin-emails-download"
              className="btn-ghost"
              style={{ padding: "0.5rem 0.9rem", fontSize: "0.7rem" }}
              disabled={emailSubs.length === 0}
            >
              <Download size={12} /> CSV
            </button>
          </div>
        </div>

        {filteredEmails.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--muted)" }}>No email subscribers yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[0.6rem] uppercase tracking-wider text-left" style={{ color: "var(--muted)" }}>
                  <th className="pb-2 font-medium">Email</th>
                  <th className="pb-2 font-medium">Source</th>
                  <th className="pb-2 font-medium">Subscribed</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmails.map((s, i) => (
                  <tr key={s.email + i} className="border-t" style={{ borderColor: "var(--border-subtle)" }} data-testid={`admin-email-row-${s.email}`}>
                    <td className="py-3 pr-3" style={{ color: "var(--ink)" }}>{s.email}</td>
                    <td className="py-3 pr-3 text-xs uppercase tracking-wider" style={{ color: "var(--muted)" }}>{s.source || "site"}</td>
                    <td className="py-3" style={{ color: "var(--ink-2)" }}>{fmtDate(s.subscribed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
